import React, { FC, ReactNode, useId, useState } from 'react';
import { DropdownItemSimple, DropdownMenu, PLAYER_SETTINGS_DROPDOWN_EVENT } from 'components/DropdownMenu/DropdownMenu';
import { DropdownTriggerButton } from 'components/DropdownMenu/DropdownMenu.css';
import { usePlayer } from 'providers/player/PlayerContext';
import translate from 'utils/fnTranslate';
import { useConfig } from 'providers/useConfig/ConfigContext';
import { BITRATE_TRACK_AUTO, BitrateTrack, PlayerControl, SUBTITLE_NONE, Track } from 'types/Player';
import { ControlGroup } from 'components/Player/PlayerWrapper';
import icons from 'style';
import { compareBitrateTracks, compareTracks, getLanguage } from 'utils/fnData';
import { HEARING_IMPAIRED_ROLES, VISUALLY_IMPAIRED_ROLES } from 'providers/player/playerConstants';
import { setSessionAudioLanguage, setSessionSubtitlesLanguage } from 'providers/player/sessionLanguages';
import useYouboraContentTracking from 'hooks/useYouboraContentTracking';
import { guardedSessionStorage } from 'utils/fnStorage';
import {
    BlockTitle,
    CloseButton,
    OptionContainer,
    StyledSVGInline,
    TrackBlock,
    TrackCol,
    TrackNotAvailable,
    TrackOption,
    TrackOptionDetails,
    TrackSelectorContentWrapper,
    TrackSelectorMobileWrapper,
    TrackSelectorWrapper,
    TrackTitle,
} from './PlayerTrackSelector.css';

const MAX_FLYOUT_HEIGHT = 348;

type BitrateTrackLabel = { quality: string; bitrate?: string };
type TrackLabel = { language: string; details?: string };

const joinTrackLabel = ({ language, details }: TrackLabel) => [language, details].filter(Boolean).join(' - ');

enum ResolutionType {
    SD = 'SD',
    HD = 'HD',
    '4K' = '4K',
}

const useTrackSelector = () => {
    const { config } = useConfig();
    const {
        getTextLanguages,
        getAudioLanguages,
        getBitrateTracks,
        setTextLanguage,
        setAudioLanguage,
        setBitrateStream,
        subtitleTrack,
        audioTrack,
        bitrateTrack,
        asset,
    } = usePlayer();

    const supportedLanguages = Object.keys(config.app_config.languages);

    const audioLanguages = getAudioLanguages();
    const subtitles = getTextLanguages();
    const bitrateTracks = getBitrateTracks();

    const audioKeys = Object.keys(audioLanguages);
    const subtitleKeys = Object.keys(subtitles);

    const getAudioAndSubtitleLabel = (track: Track, type: 'audio' | 'text'): TrackLabel => {
        const languageParts = [];

        if (track.language === SUBTITLE_NONE) {
            languageParts.push(subtitles.length > 1 ? translate('SCREEN_AUDIO_SUBTITLES_SUBTITLES_OFF') : translate('SUBTITLES_NONE'));
        } else if (type === 'audio' && !supportedLanguages.includes(track.language)) {
            languageParts.push(`${translate('SCREEN_AUDIO_OTHER_LANGUAGE')} (${track.language})`);
        } else {
            languageParts.push(getLanguage(track.language, translate(`LANGUAGE_${track.language.toUpperCase()}`)));
        }

        if (type === 'text' && track.role != null && HEARING_IMPAIRED_ROLES.includes(track.role)) {
            languageParts.push(translate('SCREEN_HARD_OF_HEARING'));
        }

        if (type === 'audio' && track.channelsCount === 2) {
            languageParts.push(translate('SCREEN_AUDIO_DOLBY_DIGITAL'));
        }

        if (type === 'audio' && track.channelsCount === 6) {
            languageParts.push(translate('SCREEN_AUDIO_DOLBY_DIGITAL_PLUS'));
        }

        const language = languageParts.join(' ');

        if (type === 'audio' && track.role != null && VISUALLY_IMPAIRED_ROLES.includes(track.role)) {
            return { language, details: translate('SCREEN_AUDIO_ACCESSIBILITY') };
        }

        return { language };
    };

    const getBitrateTrackLabel = (track: BitrateTrack): BitrateTrackLabel => {
        const parts = [];

        if (track.height != null) {
            parts.push(`${track.height}p`);
        }

        if (track.height) {
            if (track.height > 1080) {
                parts.push(ResolutionType['4K']);
            }
            if (track.height >= 720 && track.height <= 1080) {
                parts.push(ResolutionType.HD);
            }
            if (track.height < 720) {
                parts.push(ResolutionType.SD);
            }
        }

        if (track.bandwidth === BITRATE_TRACK_AUTO) {
            parts.push(translate('SCREEN_PLAYER_VIDEO_QUALITY_AUTO'));

            return { quality: parts.join(' ') };
        }

        return { quality: parts.join(' '), bitrate: `${Math.round(track.bandwidth / 1000000)}.0 Mbps` };
    };

    const setSubtitle = (textLanguage) => {
        setTextLanguage(textLanguage);
        if (textLanguage?.language) {
            setSessionSubtitlesLanguage(textLanguage.language);
        }
    };

    const setAudio = (audioLanguage) => {
        setAudioLanguage(audioLanguage);
        if (audioLanguage?.language) {
            setSessionAudioLanguage(audioLanguage.language);
        }
    };

    return {
        asset,
        subtitleKeys,
        audioKeys,
        bitrateTracks: bitrateTracks.filter((track) => track.bandwidth !== 0),

        audioLanguages,
        subtitles,

        supportedLanguages,
        getAudioAndSubtitleLabel,
        getBitrateTrackLabel,
        setTextLanguage: setSubtitle,
        setAudioLanguage: setAudio,
        setBitrateTrack: (selectedTrack) => {
            setBitrateStream(selectedTrack);
        },
        selectedSubtitle: subtitleTrack,
        selectedAudio: audioTrack,
        selectedBitrateTrack: bitrateTrack,
    };
};

export const PlayerTrackSelector: FC<{ triggerButton: ReactNode }> = ({ triggerButton }) => {
    const {
        asset,
        audioLanguages,
        subtitles,
        bitrateTracks,

        selectedAudio,
        setAudioLanguage,

        selectedSubtitle,
        setTextLanguage,

        selectedBitrateTrack,
        setBitrateTrack,

        getAudioAndSubtitleLabel,
        getBitrateTrackLabel,
    } = useTrackSelector();

    const { trackPlayerInteraction } = useYouboraContentTracking();
    const { setActiveControlGroup } = usePlayer();
    const groupId = useId();
    const [isOpen, setIsOpen] = useState(false);

    const onVisibilityChange = (isVisible) => {
        guardedSessionStorage.setItem('e_smv', isVisible);
        setIsOpen(isVisible);
        setActiveControlGroup(isVisible ? ControlGroup.AUDIO_SUBTITLE : ControlGroup.BASE);
    };

    const handleAudioLanguageChange = (audioTrack) => {
        trackPlayerInteraction('Desktop - Switch Audio', asset.id);
        setAudioLanguage(audioTrack);
    };

    const handleSubtitleChange = (subtitleTrack) => {
        trackPlayerInteraction('Desktop - Switch Subtitle', asset.id);
        setTextLanguage(subtitleTrack);
    };

    const handleBitrateChange = (bitrateTrack) => {
        trackPlayerInteraction('Desktop - Switch Quality', asset.id);
        setBitrateTrack(bitrateTrack);
    };

    const renderTrackLabel = ({ language, details }: TrackLabel) => (
        <TrackOption>
            {language}
            {details && <TrackOptionDetails>{details}</TrackOptionDetails>}
        </TrackOption>
    );

    const audioNodes: ReactNode[] =
        audioLanguages.length > 0
            ? audioLanguages.map((audioTrack, index) => (
                  <DropdownItemSimple
                      key={index}
                      selected={compareTracks(audioTrack, selectedAudio)}
                      onClick={() => handleAudioLanguageChange(audioTrack)}
                  >
                      {renderTrackLabel(getAudioAndSubtitleLabel(audioTrack, 'audio'))}
                  </DropdownItemSimple>
              ))
            : [<TrackNotAvailable key="n/a">{translate('EPG_NO_INFO')}</TrackNotAvailable>];

    const subtitleNodes: ReactNode[] =
        subtitles.length > 0
            ? subtitles.map((subtitleTrack, index) => (
                  <DropdownItemSimple
                      key={index}
                      selected={compareTracks(selectedSubtitle, subtitleTrack)}
                      onClick={() => handleSubtitleChange(subtitleTrack)}
                  >
                      {renderTrackLabel(getAudioAndSubtitleLabel(subtitleTrack, 'text'))}
                  </DropdownItemSimple>
              ))
            : [<TrackNotAvailable key="n/a">{translate('EPG_NO_INFO')}</TrackNotAvailable>];

    const bitrateTrackNodes: ReactNode[] =
        bitrateTracks.length > 0
            ? bitrateTracks.map((bitrateTrack, index) => {
                  const { quality, bitrate } = getBitrateTrackLabel(bitrateTrack);

                  return (
                      <DropdownItemSimple
                          key={index}
                          selected={compareBitrateTracks(selectedBitrateTrack, bitrateTrack)}
                          onClick={() => handleBitrateChange(bitrateTrack)}
                      >
                          <TrackOption>
                              {quality}
                              {bitrate && <TrackOptionDetails>{bitrate}</TrackOptionDetails>}
                          </TrackOption>
                      </DropdownItemSimple>
                  );
              })
            : [<TrackNotAvailable key="n/a">{translate('EPG_NO_INFO')}</TrackNotAvailable>];

    return (
        <TrackSelectorWrapper>
            <DropdownMenu
                triggerButton={
                    <DropdownTriggerButton
                        type="button"
                        data-popup-trigger
                        aria-label={translate('SCREEN_PLAYER_SETTINGS_BUTTON')}
                        aria-expanded={isOpen}
                    >
                        {triggerButton}
                    </DropdownTriggerButton>
                }
                horizontalPosition={'right'}
                verticalPosition={'top'}
                maxHeight={MAX_FLYOUT_HEIGHT}
                stripMobilePortal={true}
                triggerEventName={PLAYER_SETTINGS_DROPDOWN_EVENT}
                visibilityChangeCallback={onVisibilityChange}
                ignoreInsideClick
                closeOnOutsideClickOnly
            >
                <TrackSelectorContentWrapper>
                    <TrackBlock>
                        <TrackCol role="group" aria-labelledby={`${groupId}-audio`}>
                            <TrackTitle id={`${groupId}-audio`}>{translate('SCREEN_AUDIO_SUBTITLES_AUDIO_LANGUAGE')}</TrackTitle>
                            {audioNodes}
                        </TrackCol>
                        <TrackCol role="group" aria-labelledby={`${groupId}-subtitles`}>
                            <TrackTitle id={`${groupId}-subtitles`}>{translate('SCREEN_AUDIO_SUBTITLES_SUBTITLES')}</TrackTitle>
                            {subtitleNodes}
                        </TrackCol>
                        <TrackCol role="group" aria-labelledby={`${groupId}-quality`}>
                            <TrackTitle id={`${groupId}-quality`}>{translate('SCREEN_PLAYER_VIDEO_QUALITY')}</TrackTitle>
                            {bitrateTrackNodes}
                        </TrackCol>
                    </TrackBlock>
                </TrackSelectorContentWrapper>
            </DropdownMenu>
        </TrackSelectorWrapper>
    );
};

export const PlayerTrackSelectorMobile: FC = () => {
    const { activeControlGroup, setActiveControlGroup } = usePlayer();
    const groupId = useId();
    const {
        asset,
        audioLanguages,
        subtitles,
        bitrateTracks,

        selectedAudio,
        setAudioLanguage,

        selectedSubtitle,
        setTextLanguage,

        selectedBitrateTrack,
        setBitrateTrack,

        getAudioAndSubtitleLabel,
        getBitrateTrackLabel,
    } = useTrackSelector();

    const { trackPlayerInteraction } = useYouboraContentTracking();

    if (activeControlGroup !== ControlGroup.AUDIO_SUBTITLE) {
        return null;
    }

    const setSelectedAudio = (audioTrack) => {
        trackPlayerInteraction('Mobile - Switch Audio', asset.id);
        setAudioLanguage(audioTrack);
        setActiveControlGroup(ControlGroup.BASE);
    };

    const setSelectedSubtitle = (subtitleTrack) => {
        trackPlayerInteraction('Mobile - Switch Subtitle', asset.id);
        setTextLanguage(subtitleTrack);
        setActiveControlGroup(ControlGroup.BASE);
    };

    const setSelectedBitrate = (bitrateTrack) => {
        trackPlayerInteraction('Mobile - Switch Quality', asset.id);
        setBitrateTrack(bitrateTrack);
        setActiveControlGroup(ControlGroup.BASE);
    };

    const closeOverlay = () => {
        setActiveControlGroup(ControlGroup.BASE);
    };

    const audioNodes: ReactNode[] =
        audioLanguages.length > 0
            ? audioLanguages.map((audioTrack, index) => (
                  <DropdownItemSimple
                      key={index}
                      selected={compareTracks(audioTrack, selectedAudio)}
                      onClick={() => setSelectedAudio(audioTrack)}
                  >
                      {joinTrackLabel(getAudioAndSubtitleLabel(audioTrack, 'audio'))}
                  </DropdownItemSimple>
              ))
            : [];

    const subtitleNodes: ReactNode[] =
        subtitles.length > 0
            ? subtitles.map((subtitleTrack, index) => (
                  <DropdownItemSimple
                      key={index}
                      selected={compareTracks(selectedSubtitle, subtitleTrack)}
                      onClick={() => setSelectedSubtitle(subtitleTrack)}
                  >
                      {joinTrackLabel(getAudioAndSubtitleLabel(subtitleTrack, 'text'))}
                  </DropdownItemSimple>
              ))
            : [];

    const bitrateTrackNodes: ReactNode[] =
        bitrateTracks.length > 0
            ? bitrateTracks.map((bitrateTrack, index) => {
                  const { quality, bitrate } = getBitrateTrackLabel(bitrateTrack);

                  return (
                      <DropdownItemSimple
                          key={index}
                          selected={compareBitrateTracks(selectedBitrateTrack, bitrateTrack)}
                          onClick={() => setSelectedBitrate(bitrateTrack)}
                      >
                          {[quality, bitrate].filter(Boolean).join(' ')}
                      </DropdownItemSimple>
                  );
              })
            : [];

    return (
        <TrackSelectorMobileWrapper>
            <CloseButton key={PlayerControl.CLOSE}>
                <StyledSVGInline svg={icons.closeIcon} onClick={closeOverlay} />
            </CloseButton>
            <TrackSelectorContentWrapper>
                {audioNodes.length > 0 && (
                    <TrackBlock role="group" aria-labelledby={`${groupId}-audio`}>
                        <BlockTitle id={`${groupId}-audio`}>{translate('SCREEN_AUDIO_SUBTITLES_AUDIO_LANGUAGE')}</BlockTitle>
                        <OptionContainer>{audioNodes}</OptionContainer>
                    </TrackBlock>
                )}
                {subtitleNodes.length > 0 && (
                    <TrackBlock role="group" aria-labelledby={`${groupId}-subtitles`}>
                        <BlockTitle id={`${groupId}-subtitles`}>{translate('SCREEN_AUDIO_SUBTITLES_SUBTITLES')}</BlockTitle>
                        <OptionContainer>{subtitleNodes}</OptionContainer>
                    </TrackBlock>
                )}
                {bitrateTrackNodes.length > 0 && (
                    <TrackBlock role="group" aria-labelledby={`${groupId}-quality`}>
                        <BlockTitle id={`${groupId}-quality`}>{translate('SCREEN_PLAYER_VIDEO_QUALITY')}</BlockTitle>
                        <OptionContainer>{bitrateTrackNodes}</OptionContainer>
                    </TrackBlock>
                )}
            </TrackSelectorContentWrapper>
        </TrackSelectorMobileWrapper>
    );
};
